'use client';

/**
 * Partnership Inquiry Page
 * Brand partnership form with RFP submission
 */

import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { motion } from 'framer-motion';
import { Building2, Send, TrendingUp, Users, Heart, CheckCircle } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import Image from 'next/image';
import { brandMetrics } from '@/lib/data';

export default function PartnershipsPage() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // Mock submission delay
    await new Promise((resolve) => setTimeout(resolve, 1500));

    setSubmitted(true);
    setLoading(false);

    toast.success('Partnership Inquiry Submitted!', {
      description: "We'll be in touch within 24 hours.",
    });
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-2xl"
        >
          <Card className="p-12 text-center">
            <div className="w-20 h-20 bg-gradient-electric rounded-full flex items-center justify-center mx-auto mb-6 shadow-neon">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-3xl font-black mb-4 uppercase">
              Thank You for Your Interest!
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              We've received your partnership inquiry and will reach out within 24
              hours to discuss how we can create culture together.
            </p>
            <Button asChild size="lg">
              <a href="/">Return Home</a>
            </Button>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <Image
            src="/images/generated/brand-partnership-meeting.png"
            alt="Brand partnership"
            fill
            className="object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/80 to-background/60" />

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl"
          >
            <div className="flex items-center space-x-3 mb-6">
              <Building2 className="w-12 h-12 text-primary" />
              <h1 className="text-5xl md:text-7xl font-black uppercase">
                Partner <span className="text-gradient-electric">With Us</span>
              </h1>
            </div>
            <p className="text-xl md:text-2xl text-foreground/80 mb-8">
              Create revenue-driven experiential activations that amplify brand
              awareness and forge authentic cultural connections.
            </p>

            {/* Key Stats */}
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="p-6 bg-gradient-to-br from-primary/10 to-transparent border-primary/50">
                <TrendingUp className="w-8 h-8 text-primary mb-3" />
                <p className="text-3xl font-black mb-1">{brandMetrics.avgBrandLift}</p>
                <p className="text-sm text-muted-foreground">Avg Brand Lift</p>
              </Card>
              <Card className="p-6 bg-gradient-to-br from-secondary/10 to-transparent border-secondary/50">
                <Users className="w-8 h-8 text-secondary mb-3" />
                <p className="text-3xl font-black mb-1">
                  {(brandMetrics.totalAttendees / 1000).toFixed(0)}K+
                </p>
                <p className="text-sm text-muted-foreground">Total Attendees</p>
              </Card>
              <Card className="p-6 bg-gradient-to-br from-accent/10 to-transparent border-accent/50">
                <Heart className="w-8 h-8 text-accent mb-3" />
                <p className="text-3xl font-black mb-1">{brandMetrics.partnerBrands.length}+</p>
                <p className="text-sm text-muted-foreground">Partner Brands</p>
              </Card>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card className="p-8 md:p-12">
                <h2 className="text-3xl font-black mb-6 uppercase">
                  Start a <span className="text-gradient-sunset">Conversation</span>
                </h2>
                <p className="text-muted-foreground mb-8">
                  Tell us about your brand and vision. We'll work together to create
                  an experiential activation that drives real impact.
                </p>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="firstName">First Name *</Label>
                      <Input
                        id="firstName"
                        placeholder="John"
                        required
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="lastName">Last Name *</Label>
                      <Input
                        id="lastName"
                        placeholder="Doe"
                        required
                        className="mt-2"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="john@company.com"
                      required
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label htmlFor="company">Company Name *</Label>
                    <Input
                      id="company"
                      placeholder="Your Company"
                      required
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label htmlFor="title">Your Title</Label>
                    <Input
                      id="title"
                      placeholder="Marketing Director"
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label htmlFor="budget">Estimated Budget Range</Label>
                    <Input
                      id="budget"
                      placeholder="$50k - $100k"
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label htmlFor="timeline">Desired Timeline</Label>
                    <Input
                      id="timeline"
                      placeholder="Q3 2026"
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message">Project Brief *</Label>
                    <Textarea
                      id="message"
                      placeholder="Tell us about your brand, target audience, and activation goals..."
                      rows={6}
                      required
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label htmlFor="attachment">
                      Attach RFP or Brief (Optional)
                    </Label>
                    <Input
                      id="attachment"
                      type="file"
                      accept=".pdf,.doc,.docx"
                      className="mt-2"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-14 text-lg font-black uppercase gradient-electric shadow-neon"
                  >
                    {loading ? (
                      'Submitting...'
                    ) : (
                      <>
                        <Send className="mr-2 w-5 h-5" />
                        Submit Partnership Inquiry
                      </>
                    )}
                  </Button>
                </form>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Partner Brands Section */}
      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-6xl font-black mb-4 uppercase">
              Trusted By <span className="text-gradient-electric">The Best</span>
            </h2>
            <p className="text-xl text-muted-foreground">
              Join brands creating culture-forward experiences
            </p>
          </motion.div>

          <div className="flex flex-wrap items-center justify-center gap-8">
            {brandMetrics.partnerBrands.map((brand, index) => (
              <motion.div
                key={brand}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-2xl font-black text-muted-foreground/40 hover:text-primary transition-colors"
              >
                {brand}
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
